import { useState, useEffect, useMemo } from 'react';

/**
 * 检测设备类型
 * @param {number} breakpoint - PC/Mobile 分界点宽度
 * @returns {'mobile' | 'pc'}
 */
const detectDeviceType = (breakpoint = 768) => {
  // SSR 环境检查
  if (typeof window === 'undefined') {
    return 'pc';
  }

  try {
    // 1. 优先使用现代 API
    if (navigator.userAgentData?.mobile) {
      return 'mobile';
    }

    // 2. User Agent 检测
    const ua = navigator.userAgent;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    
    // 3. 屏幕宽度检测
    const width = window.innerWidth;
    const isSmallScreen = width < breakpoint;

    // 4. 触摸支持检测
    const hasTouch = 'ontouchstart' in window || 
                     navigator.maxTouchPoints > 0;

    // 5. 指针类型检测（排除触摸屏笔记本）
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

    // 综合判断
    if (isMobileUA) {
      return 'mobile';
    }
    
    if (isSmallScreen && (hasTouch || hasCoarsePointer)) {
      return 'mobile';
    }

    if (isSmallScreen) {
      return 'mobile';
    }

    return 'pc';
  } catch (error) {
    console.error('设备检测失败:', error);
    return 'pc';
  }
};

/**
 * 判断设备类型的自定义 Hook
 * 
 * @param {Object} options - 配置选项
 * @param {number} [options.breakpoint=768] - PC/Mobile 分界点（像素）
 * @param {number} [options.debounceMs=200] - resize 防抖延迟（毫秒）
 * @returns {'mobile' | 'pc'} 设备类型
 * 
 * @example
 * // 基础用法
 * const deviceType = useDeviceType();
 * 
 * // 自定义分界点
 * const deviceType = useDeviceType({ breakpoint: 1024 });
 * 
 * // 使用示例
 * {deviceType === 'mobile' ? <MobileView /> : <PCView />}
 */
export const useDeviceType = ({ breakpoint = 768, debounceMs = 200 } = {}) => {
  // 初始化设备类型
  const [deviceType, setDeviceType] = useState(() => detectDeviceType(breakpoint));

  // 防抖定时器引用
  const debounceTimer = useMemo(() => ({ current: null }), []);

  useEffect(() => {
    const handleResize = () => {
      // 清除之前的定时器
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // 设置新的防抖定时器
      debounceTimer.current = setTimeout(() => {
        const newType = detectDeviceType(breakpoint);
        setDeviceType(prevType => {
          // 只在类型真正改变时更新
          return prevType !== newType ? newType : prevType;
        });
      }, debounceMs);
    };

    // 监听窗口变化
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // 清理函数
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [breakpoint, debounceMs, debounceTimer]);
  console.log('当前设备类型', deviceType)
  return deviceType;
};

/**
 * 判断是否为移动设备
 * @param {Object} options - 配置选项
 * @returns {boolean} 是否为移动设备
 * 
 * @example
 * const isMobile = useIsMobile();
 * {isMobile && <MobileOnlyComponent />}
 */
export const useIsMobile = (options) => {
  return useDeviceType(options) === 'mobile';
};

/**
 * 判断是否为 PC 设备
 * @param {Object} options - 配置选项
 * @returns {boolean} 是否为 PC 设备
 * 
 * @example
 * const isPC = useIsPC();
 * {isPC && <PCOnlyComponent />}
 */
export const useIsPC = (options) => {
  return useDeviceType(options) === 'pc';
};
